import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Users, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import {
  cartAdminService,
  customerService,
  type CartProductAgg,
  type CartSummaryRow,
  type CustomerCart,
} from '../services/customerService';
import { formatMoney } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

type Tab = 'products' | 'carts';

export function CartsPage() {
  const [tab, setTab] = useState<Tab>('products');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<CartProductAgg[]>([]);
  const [carts, setCarts] = useState<CartSummaryRow[]>([]);
  const [cartPage, setCartPage] = useState(1);
  const [cartTotalPages, setCartTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedCart, setSelectedCart] = useState<CustomerCart | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const itemsPerPage = 15;

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cartAdminService.listProducts();
      setProducts(data ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت آمار سبدها');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCarts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cartAdminService.listCarts({ page: cartPage, limit: itemsPerPage });
      setCarts(res.data ?? []);
      setCartTotalPages(Math.max(1, res.totalPages));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت سبدها');
      setCarts([]);
    } finally {
      setLoading(false);
    }
  }, [cartPage]);

  useEffect(() => {
    if (tab === 'products') void loadProducts();
    else void loadCarts();
  }, [tab, loadProducts, loadCarts]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    return products.filter(
      (p) => p.name.includes(searchTerm) || p.sku?.includes(searchTerm) || p.slug?.includes(searchTerm),
    );
  }, [products, searchTerm]);

  const filteredCarts = useMemo(() => {
    if (!searchTerm) return carts;
    return carts.filter(
      (c) =>
        (c.customerName ?? '').includes(searchTerm) ||
        (c.customerPhone ?? '').includes(searchTerm),
    );
  }, [carts, searchTerm]);

  const openCart = async (customerId: string) => {
    setDetailLoading(true);
    setSelectedCart(null);
    try {
      const cart = await customerService.getCustomerCart(customerId);
      setSelectedCart(cart);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت سبد');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">سبدهای خرید</h1>
        <p className="text-slate-600 mt-1">
          تعداد هر محصول در سبد مشتریان و فهرست سبدهای فعال
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setTab('products');
            setSearchTerm('');
            setSelectedCart(null);
          }}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
            tab === 'products'
              ? 'bg-indigo-600 text-white'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          <Package className="h-4 w-4" />
          محصولات در سبدها
        </button>
        <button
          type="button"
          onClick={() => {
            setTab('carts');
            setSearchTerm('');
            setSelectedCart(null);
          }}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
            tab === 'carts'
              ? 'bg-indigo-600 text-white'
              : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
          }`}
        >
          <Users className="h-4 w-4" />
          سبد مشتریان
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder={tab === 'products' ? 'جستجو نام یا SKU محصول...' : 'جستجو نام یا موبایل مشتری...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right"
            dir="rtl"
          />
        </div>
      </div>

      {tab === 'products' ? (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="admin-table-scroll">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">محصول</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">SKU</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">تعداد در سبدها</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">تعداد سبد</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">موجودی</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">قیمت</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                      در حال بارگذاری...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                      فعلاً محصولی در سبد خرید مشتریان نیست
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((p, index) => (
                    <tr key={p.productId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm tabular-nums text-slate-500">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {p.image ? (
                            <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" />
                          ) : (
                            <div className="h-10 w-10 rounded bg-slate-100" />
                          )}
                          <p className="text-sm font-medium text-slate-900">{p.name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600" dir="ltr">
                        {p.sku || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-indigo-700">{p.totalQty}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{p.cartCount}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{p.stock}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatMoney(Number(p.discountPrice ?? p.price))} تومان
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="admin-table-scroll">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">مشتری</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">اقلام</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">تعداد</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">بروزرسانی</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                          در حال بارگذاری...
                        </td>
                      </tr>
                    ) : filteredCarts.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                          سبد فعالی یافت نشد
                        </td>
                      </tr>
                    ) : (
                      filteredCarts.map((c, index) => (
                        <tr
                          key={c.id}
                          className="border-b border-slate-100 hover:bg-indigo-50 cursor-pointer"
                          onClick={() => void openCart(c.customerId)}
                        >
                          <td className="px-4 py-3 text-sm tabular-nums text-slate-500">
                            {rowNumber(index, cartPage, itemsPerPage)}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-900">{c.customerName}</p>
                            <p className="text-xs text-slate-500" dir="ltr">
                              {c.customerPhone}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{c.itemCount}</td>
                          <td className="px-4 py-3 text-sm font-bold text-indigo-700">{c.totalQty}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {c.updatedAt ? new Date(c.updatedAt).toLocaleString('fa-IR') : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-center py-4">
                <Pagination
                  currentPage={cartPage}
                  totalPages={cartTotalPages}
                  onPageChange={setCartPage}
                />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4 min-h-[280px]">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">جزئیات سبد</h2>
              {detailLoading ? (
                <p className="text-center text-slate-500 py-10">در حال بارگذاری...</p>
              ) : !selectedCart ? (
                <p className="text-center text-slate-500 py-10">یک سبد را از لیست انتخاب کنید</p>
              ) : selectedCart.items.length === 0 ? (
                <p className="text-center text-slate-500 py-10">این سبد خالی است</p>
              ) : (
                <div className="space-y-3">
                  {selectedCart.customer && (
                    <p className="text-sm text-slate-600 mb-2">
                      {selectedCart.customer.name} ·{' '}
                      <span dir="ltr">{selectedCart.customer.phone}</span>
                    </p>
                  )}
                  {selectedCart.items.map((it) => {
                    const options = [it.color, it.variant].filter(Boolean).join(' · ');
                    return (
                    <div
                      key={`${it.productId}-${it.color ?? ''}-${it.variant ?? ''}`}
                      className="flex items-center gap-3 border-b border-slate-100 pb-3"
                    >
                      {it.image ? (
                        <img src={it.image} alt="" className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded bg-slate-100" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{it.name}</p>
                        {options ? (
                          <p className="text-xs text-indigo-600 mt-0.5">
                            {it.color ? `رنگ: ${it.color}` : ''}
                            {it.color && it.variant ? ' · ' : ''}
                            {it.variant ? `نوع: ${it.variant}` : ''}
                          </p>
                        ) : null}
                        <p className="text-xs text-slate-500">
                          {formatMoney(Number(it.discountPrice ?? it.price))} تومان
                        </p>
                      </div>
                      <div className="text-sm font-bold text-indigo-700">×{it.qty}</div>
                    </div>
                    );
                  })}
                  <p className="text-sm text-slate-600 pt-2">
                    جمع اقلام: {selectedCart.totalQty} عدد در {selectedCart.itemCount} ردیف
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
